import React, { useEffect, useState, useMemo } from 'react';
import ReactDOM from 'react-dom';
import { applyFrontendSettings, loadFrontendSettings } from '../utils/frontendSettings';
import { useNdTheme } from '../utils/ndTheme';
import '../styles/newDesign.css';

// Единая палитра типов события для сетки, легенды и вложенных окон
const ND_LEAVE_TYPES = {
  vacation: { label: 'Отпуск', emoji: '🌴', cls: 'nd-t-vacation' },
  leave: { label: 'Отгул', emoji: '🚶', cls: 'nd-t-leave' },
  sick: { label: 'Больничный', emoji: '🚩', cls: 'nd-t-sick' },
  remote: { label: 'На удаленке', emoji: '🏠', cls: 'nd-t-remote' },
};

const ND_STATUS_LABELS = {
  approved: 'Одобрено',
  pending: 'Ожидает',
  completed: 'Отработано',
  rejected: 'Отклонено',
};

const months = [
  'Январь','Февраль','Март','Апрель','Май','Июнь','Июль','Август','Сентябрь','Октябрь','Ноябрь','Декабрь'
];

const years = Array.from({length: 5}, (_,i) => new Date().getFullYear()-2+i);
const weekDays = ['Вс','Пн','Вт','Ср','Чт','Пт','Сб'];

// Утилиты
function getDaysMatrix(year, month) {
  const firstDay = new Date(year, month, 1);
  const lastDay = new Date(year, month+1, 0);
  const matrix = [];
  let week = [];
  let dayOfWeek = firstDay.getDay();
  
  for(let i=0;i<dayOfWeek;i++) week.push(null);
  for(let d=1;d<=lastDay.getDate();d++) {
    week.push(new Date(year, month, d));
    if(week.length===7) { matrix.push(week); week=[]; }
  }
  if(week.length) { 
    while(week.length<7) week.push(null); 
    matrix.push(week); 
  }
  return matrix;
}

function formatDateToYYYYMMDD(date) {
  if (!date) return '';
  const d = new Date(date);
  const year = d.getFullYear();
  const month = String(d.getMonth() + 1).padStart(2, '0');
  const day = String(d.getDate()).padStart(2, '0');
  return `${year}-${month}-${day}`;
}

function isDateInRange(targetDate, startDate, endDate) {
  const target = new Date(targetDate);
  const start = new Date(startDate);
  const end = new Date(endDate);
  
  target.setHours(0, 0, 0, 0);
  start.setHours(0, 0, 0, 0);
  end.setHours(0, 0, 0, 0);
  
  return target >= start && target <= end;
}

function isToday(d) {
  if (!d) return false;
  const t = new Date();
  return d.getDate() === t.getDate() && d.getMonth() === t.getMonth() && d.getFullYear() === t.getFullYear();
}

export default function AllLeavesCalendar({ open, onClose, token }) {
  const { isLight: ndIsLight, toggle: toggleNdTheme } = useNdTheme();
  // Состояние для стилей фона
  const [backgroundStyle, setBackgroundStyle] = useState({
    backgroundColor: 'transparent',
    backgroundImage: 'linear-gradient(135deg, #232931 0%, #181c22 100%)',
    backgroundSize: 'cover',
    backgroundPosition: 'center',
    backgroundRepeat: 'no-repeat'
  });
  
  // Функция для применения стилей фона из настроек
  const applyBackgroundStyles = () => {
    console.log('🔄 [AllLeavesCalendar] Начинаем загрузку настроек...');
    const settings = loadFrontendSettings();
    console.log('🔍 [AllLeavesCalendar] Загружены настройки:', {
      hasSettings: !!settings,
      hasModals: !!settings?.modals,
      hasCalendar: !!settings?.modals?.calendar,
      calendarKeys: settings?.modals?.calendar ? Object.keys(settings.modals.calendar) : [],
      fullSettings: settings
    });
    
    // ВАЖНО: Не применяем applyFrontendSettings здесь, так как он устанавливает CSS переменные,
    // которые могут конфликтовать с inline стилями. Вместо этого применяем стили напрямую через state.
    // applyFrontendSettings(settings);
    
    // Получаем настройки календаря или общий фон модалок
    const calendarSettings = settings?.modals?.calendar;
    const useGlobalModalBackground = settings?.applyGlobalModalBackground !== false;
    const effectiveSettings = useGlobalModalBackground
      ? {
          background: settings?.modalBackground,
          backgroundImage: settings?.modalBackgroundImage,
          backgroundImageOpacity: settings?.modalBackgroundImageOpacity,
          useImageOnly: true
        }
      : calendarSettings;
    console.log('🔍 [AllLeavesCalendar] Настройки календаря:', {
      hasSettings: !!effectiveSettings,
      hasBackgroundImage: !!effectiveSettings?.backgroundImage && effectiveSettings.backgroundImage.trim() !== '',
      backgroundImageLength: effectiveSettings?.backgroundImage?.length || 0,
      backgroundImagePreview: effectiveSettings?.backgroundImage?.substring(0, 50) || 'нет',
      background: effectiveSettings?.background,
      opacity: effectiveSettings?.backgroundImageOpacity,
      useImageOnly: effectiveSettings?.useImageOnly,
      allKeys: effectiveSettings ? Object.keys(effectiveSettings) : []
    });
    
    if (effectiveSettings) {
      const bgStyle = {
        backgroundSize: 'cover',
        backgroundPosition: 'center',
        backgroundRepeat: 'no-repeat'
      };
      
      // Если есть backgroundImage, используем его с opacity
      if (effectiveSettings.backgroundImage && effectiveSettings.backgroundImage.trim() !== '') {
        const opacity = effectiveSettings.backgroundImageOpacity || 1;
        // Base64 строка уже содержит data:image/..., для CSS backgroundImage нужно использовать url()
        // Но для data: URLs можно использовать напрямую
        let imageUrl;
        if (effectiveSettings.backgroundImage.startsWith('data:')) {
          // Для data: URLs используем напрямую, но оборачиваем в url() для надежности
          imageUrl = `url("${effectiveSettings.backgroundImage}")`;
        } else if (effectiveSettings.backgroundImage.startsWith('url(')) {
          // Уже обернуто в url()
          imageUrl = effectiveSettings.backgroundImage;
        } else {
          // Обычный путь, оборачиваем в url()
          imageUrl = `url(${effectiveSettings.backgroundImage})`;
        }
        
        console.log('🖼️ [AllLeavesCalendar] Обработка изображения:', {
          hasImage: true,
          imageLength: effectiveSettings.backgroundImage.length,
          isBase64: effectiveSettings.backgroundImage.startsWith('data:'),
          originalPreview: effectiveSettings.backgroundImage.substring(0, 80),
          imageUrlPreview: imageUrl.substring(0, 100),
          opacity: opacity,
          useImageOnly: effectiveSettings.useImageOnly,
          finalImageUrl: imageUrl
        });
        
        // Преобразуем hex цвет в rgba для overlay
        const baseColor = effectiveSettings.background || '#232931';
        const hexToRgba = (hex, alpha) => {
          // Убираем # если есть
          const cleanHex = hex.replace('#', '');
          if (cleanHex.length !== 6) {
            console.warn('⚠️ [AllLeavesCalendar] Неверный формат цвета:', hex);
            return `rgba(35, 41, 49, ${alpha})`; // fallback
          }
          const r = parseInt(cleanHex.slice(0, 2), 16);
          const g = parseInt(cleanHex.slice(2, 4), 16);
          const b = parseInt(cleanHex.slice(4, 6), 16);
          return `rgba(${r}, ${g}, ${b}, ${alpha})`;
        };
        
        // Проверяем, нужно ли использовать только изображение (игнорировать цвет фона)
        // По умолчанию, если есть изображение, используем только его (useImageOnly = true)
        const useImageOnly = effectiveSettings.useImageOnly !== undefined ? effectiveSettings.useImageOnly : true;
        
        console.log('🎨 [AllLeavesCalendar] Применение изображения:', {
          useImageOnly,
          hasBaseColor: !!baseColor,
          opacity,
          willUseColor: !useImageOnly
        });
        
        // Если opacity меньше 1, добавляем overlay для затемнения/осветления
        if (opacity < 1) {
          if (useImageOnly) {
            // Используем только градиент для opacity, без цвета фона
            bgStyle.backgroundColor = 'transparent';
            bgStyle.backgroundImage = `linear-gradient(rgba(0, 0, 0, ${1 - opacity}), rgba(0, 0, 0, ${1 - opacity})), ${imageUrl}`;
          } else {
            // Используем градиент поверх изображения для контроля opacity с цветом фона
            bgStyle.backgroundColor = baseColor;
            bgStyle.backgroundImage = `linear-gradient(${hexToRgba(baseColor, 1 - opacity)}, ${hexToRgba(baseColor, 1 - opacity)}), ${imageUrl}`;
          }
        } else {
          // Полная непрозрачность
          if (useImageOnly) {
            // Используем только изображение, без цвета фона
            bgStyle.backgroundColor = 'transparent';
            bgStyle.backgroundImage = imageUrl;
          } else {
            // Используем изображение с цветом фона как fallback
            bgStyle.backgroundColor = baseColor;
            bgStyle.backgroundImage = imageUrl;
          }
        }
      } else {
        // Если нет изображения, используем только цвет фона
        const fallbackBackground = effectiveSettings.background || 'linear-gradient(135deg, #232931 0%, #181c22 100%)';
        console.log('🎨 [AllLeavesCalendar] Обработка цвета фона (без изображения):', {
          fallbackBackground,
          isGradient: fallbackBackground.includes('gradient'),
          isRgb: fallbackBackground.includes('rgb'),
          isHex: fallbackBackground.startsWith('#')
        });
        
        // Если это градиент, используем его как backgroundImage, иначе как backgroundColor
        if (fallbackBackground.includes('gradient')) {
          bgStyle.backgroundColor = 'transparent';
          bgStyle.backgroundImage = fallbackBackground;
        } else if (fallbackBackground.includes('rgb') || fallbackBackground.startsWith('#')) {
          // Это цвет (rgb, rgba, hex)
          bgStyle.backgroundColor = fallbackBackground;
          bgStyle.backgroundImage = 'none';
        } else {
          // Fallback на градиент по умолчанию
          bgStyle.backgroundColor = 'transparent';
          bgStyle.backgroundImage = 'linear-gradient(135deg, #232931 0%, #181c22 100%)';
        }
      }
      
      console.log('✅ [AllLeavesCalendar] Финальные стили фона:', {
        backgroundColor: bgStyle.backgroundColor,
        backgroundImage: bgStyle.backgroundImage?.substring(0, 150),
        backgroundSize: bgStyle.backgroundSize,
        backgroundPosition: bgStyle.backgroundPosition,
        backgroundRepeat: bgStyle.backgroundRepeat,
        fullStyle: bgStyle
      });
      
      // Устанавливаем стили напрямую, чтобы они имели приоритет над CSS переменными
      setBackgroundStyle(bgStyle);
      
      // Также обновляем CSS переменные для совместимости, но с правильными значениями
      if (calendarSettings.backgroundImage && calendarSettings.backgroundImage.trim() !== '') {
        const useImageOnly = calendarSettings.useImageOnly !== undefined ? calendarSettings.useImageOnly : true;
        if (useImageOnly) {
          document.documentElement.style.setProperty('--modal-calendar-background', 'transparent');
        } else {
          document.documentElement.style.setProperty('--modal-calendar-background', calendarSettings.background || 'transparent');
        }
        const imageUrl = calendarSettings.backgroundImage.startsWith('data:') 
          ? calendarSettings.backgroundImage 
          : `url(${calendarSettings.backgroundImage})`;
        document.documentElement.style.setProperty('--modal-calendar-background-image', imageUrl);
      } else {
        const fallbackBackground = calendarSettings.background || 'linear-gradient(135deg, #232931 0%, #181c22 100%)';
        if (fallbackBackground.includes('gradient')) {
          document.documentElement.style.setProperty('--modal-calendar-background', 'transparent');
          document.documentElement.style.setProperty('--modal-calendar-background-image', fallbackBackground);
        } else {
          document.documentElement.style.setProperty('--modal-calendar-background', fallbackBackground);
          document.documentElement.style.setProperty('--modal-calendar-background-image', 'none');
        }
      }
      
      // Дополнительная проверка через небольшую задержку после применения стилей
      setTimeout(() => {
        const container = document.querySelector('[data-calendar-container]');
        if (container) {
          const computedStyle = window.getComputedStyle(container);
          console.log('🔍 [AllLeavesCalendar] Проверка примененных стилей в DOM:', {
            inlineBackgroundColor: container.style.backgroundColor,
            inlineBackgroundImage: container.style.backgroundImage?.substring(0, 150),
            inlineBackgroundSize: container.style.backgroundSize,
            inlineBackgroundPosition: container.style.backgroundPosition,
            computedBackgroundImage: computedStyle.backgroundImage?.substring(0, 150),
            computedBackgroundColor: computedStyle.backgroundColor,
            stateBackgroundColor: bgStyle.backgroundColor,
            stateBackgroundImage: bgStyle.backgroundImage?.substring(0, 150)
          });
          
          // КРИТИЧЕСКАЯ ПРОВЕРКА: Если изображение не применилось, применим его вручную
          if (bgStyle.backgroundImage && bgStyle.backgroundImage !== 'none' && !container.style.backgroundImage) {
            console.error('❌ [AllLeavesCalendar] КРИТИЧЕСКАЯ ПРОБЛЕМА: Изображение не применилось! Применяем вручную...');
            container.style.backgroundColor = bgStyle.backgroundColor || 'transparent';
            container.style.backgroundImage = bgStyle.backgroundImage;
            container.style.backgroundSize = bgStyle.backgroundSize || 'cover';
            container.style.backgroundPosition = bgStyle.backgroundPosition || 'center';
            container.style.backgroundRepeat = bgStyle.backgroundRepeat || 'no-repeat';
            container.style.backgroundAttachment = 'fixed';
            console.log('✅ [AllLeavesCalendar] Стили применены вручную:', {
              backgroundColor: container.style.backgroundColor,
              backgroundImage: container.style.backgroundImage?.substring(0, 100)
            });
          }
        } else {
          console.warn('⚠️ [AllLeavesCalendar] Элемент [data-calendar-container] не найден в DOM');
        }
      }, 500);
    } else {
      console.warn('⚠️ [AllLeavesCalendar] Настройки календаря не найдены!');
      console.warn('⚠️ [AllLeavesCalendar] Доступные настройки:', {
        hasSettings: !!settings,
        hasModals: !!settings?.modals,
        modalKeys: settings?.modals ? Object.keys(settings.modals) : []
      });
      
      // Устанавливаем стили по умолчанию, если настройки не найдены
      const defaultStyle = {
        backgroundColor: 'transparent',
        backgroundImage: 'linear-gradient(135deg, #232931 0%, #181c22 100%)',
        backgroundSize: 'cover',
        backgroundPosition: 'center',
        backgroundRepeat: 'no-repeat'
      };
      console.log('🔄 [AllLeavesCalendar] Устанавливаем стили по умолчанию:', defaultStyle);
      setBackgroundStyle(defaultStyle);
    }
  };
  
  // Применяем настройки фронтенда при открытии модалки
  useEffect(() => {
    if (open) {
      console.log('🚀 [AllLeavesCalendar] Календарь открыт, применяем настройки фона...');
      applyBackgroundStyles();
    }
  }, [open]);
  
  // Слушаем изменения в localStorage для обновления стилей без перезагрузки
  useEffect(() => {
    if (open) {
      const handleStorageChange = (e) => {
        if (e.key === 'frontendSettings') {
          console.log('🔄 [AllLeavesCalendar] Обнаружено изменение в localStorage, обновляем стили...');
          applyBackgroundStyles();
        }
      };
      
      const handleSettingsUpdated = () => {
        console.log('🔄 [AllLeavesCalendar] Получено событие frontendSettingsUpdated, обновляем стили...');
        applyBackgroundStyles();
      };
      
      window.addEventListener('storage', handleStorageChange);
      // Также слушаем кастомное событие для обновления в той же вкладке
      window.addEventListener('frontendSettingsUpdated', handleSettingsUpdated);
      
      return () => {
        window.removeEventListener('storage', handleStorageChange);
        window.removeEventListener('frontendSettingsUpdated', handleSettingsUpdated);
      };
    }
  }, [open]);
  
  // Состояния
  const [leaves, setLeaves] = useState([]);
  const [users, setUsers] = useState({});
  const [selectedUser, setSelectedUser] = useState('all');
  const [selectedType, setSelectedType] = useState('all');
  const [selectedStatus, setSelectedStatus] = useState('all');
  const [userSearch, setUserSearch] = useState('');
  const [showUserAutocomplete, setShowUserAutocomplete] = useState(false);
  const [currentUser, setCurrentUser] = useState(null);
  const [balance, setBalance] = useState(null);
  const [showAddModal, setShowAddModal] = useState(false);
  const [editLeave, setEditLeave] = useState(null);
  const [history, setHistory] = useState([]);
  const [selectedMonth, setSelectedMonth] = useState(new Date().getMonth());
  const [selectedYear, setSelectedYear] = useState(new Date().getFullYear());
  const [absentModal, setAbsentModal] = useState({ open: false, date: null, list: [] });
  const [birthdayModal, setBirthdayModal] = useState({ open: false, date: null, list: [] });
  const [calendarCollapsed, setCalendarCollapsed] = useState(false);
  const [employeesBirthdays, setEmployeesBirthdays] = useState([]);

  // Извлечь месяц/день дня рождения из сотрудника (birth_day+birth_month или birthday строка)
  const getEmployeeBirthMonthDay = (e) => {
    if (e.birth_month != null && e.birth_day != null) return { month: Number(e.birth_month), day: Number(e.birth_day) };
    const b = (e.birthday || '').trim();
    if (!b) return null;
    const iso = b.match(/^(\d{4})-(\d{2})-(\d{2})/);
    if (iso) return { month: parseInt(iso[2], 10), day: parseInt(iso[3], 10) };
    const ddmmyy = b.match(/^(\d{1,2})\.(\d{1,2})/);
    if (ddmmyy) return { month: parseInt(ddmmyy[2], 10), day: parseInt(ddmmyy[1], 10) };
    return null;
  };

  const getBirthdaysForDate = (date) => {
    if (!date || !Array.isArray(employeesBirthdays)) return [];
    const month = date.getMonth() + 1;
    const day = date.getDate();
    return employeesBirthdays.filter(e => {
      const md = getEmployeeBirthMonthDay(e);
      return md && md.month === month && md.day === day;
    });
  };

  // Определение типа события с учетом логики
  const getLogicalType = (leave) => {
    if (!leave) return 'leave';
    if (leave.type === 'vacation') return 'vacation';
    if (leave.type === 'sick') return 'sick';
    if (leave.type === 'remote') return 'remote';
    if (leave.type === 'leave') {
      const reason = String(leave.reason || '').toLowerCase();
      if (/болею|больнич/i.test(reason)) return 'sick';
      return 'leave';
    }
    return String(leave.type || 'leave');
  };

  // Проверка роли HR - проверяем разные варианты написания роли
  const isHr = currentUser && (
    currentUser.role === 'hr' || 
    currentUser.role === 'admin' || 
    currentUser.role === 'руководитель' ||
    String(currentUser.role).toLowerCase() === 'hr' ||
    String(currentUser.role).toLowerCase() === 'admin'
  );
  
  // Отладочная информация для проверки роли
  useEffect(() => {
    if (open) {
      console.log('🔍 [AllLeavesCalendar] Проверка роли пользователя:', {
        username: currentUser?.username || (currentUser?.first_name + ' ' + currentUser?.last_name) || 'не определен',
        role: currentUser?.role || 'не определена',
        roleType: typeof currentUser?.role,
        isHr: isHr,
        currentUser: currentUser
      });
      
      // Проверяем все pending заявки
      if (isHr && Array.isArray(leaves)) {
        const pendingLeaves = leaves.filter(l => l.status === 'pending');
        console.log('🔍 [AllLeavesCalendar] Найдено pending заявок:', pendingLeaves.length, pendingLeaves);
      }
    }
  }, [open, currentUser, isHr, leaves]);

  // Фильтрация отгулов
  const filteredLeaves = useMemo(() => {
    const result = Array.isArray(leaves) ? leaves.filter(l => {
      // Для HR: заявки со статусом 'pending' всегда показываются в календаре
      // независимо от всех фильтров, чтобы HR мог видеть заявки на одобрение
      const isPendingForHr = isHr && l.status === 'pending';
      
      // Пользователь/HR фильтр
      if (!isHr) {
        if (l.userId !== currentUser?.id) return false;
      } else {
        // Для HR: если это не pending заявка, применяем фильтр по пользователю
        if (!isPendingForHr && selectedUser !== 'all' && l.userId !== Number(selectedUser)) return false;
      }
      
      // Статус: для HR заявки со статусом 'pending' всегда показываются
      if (!isPendingForHr && selectedStatus !== 'all' && l.status !== selectedStatus) {
        return false;
      }
      
      // Тип события: для HR заявки со статусом 'pending' всегда показываются
      if (!isPendingForHr) {
        const logicalType = getLogicalType(l);
        if (selectedType !== 'all' && logicalType !== selectedType) return false;
      }
      
      return true;
    }) : [];
    
    // Отладочная информация для HR
    if (isHr) {
      const pendingLeaves = result.filter(l => l.status === 'pending');
      console.log('🔍 [AllLeavesCalendar] HR фильтрация:', {
        totalLeaves: leaves.length,
        filteredLeaves: result.length,
        pendingLeaves: pendingLeaves.length,
        pendingLeavesDetails: pendingLeaves.map(l => ({ id: l.id, userId: l.userId, startDate: l.startDate, endDate: l.endDate, status: l.status })),
        isHr,
        selectedStatus,
        selectedUser,
        selectedType
      });
    }
    
    return result;
  }, [leaves, isHr, currentUser, selectedUser, selectedStatus, selectedType]);

  // Подсчет статистики месяца
  const monthlyStats = useMemo(() => {
    const stats = { vacation: 0, leave: 0, sick: 0, remote: 0 };
    const processedLeaves = new Set();
    
    console.log('=== ПОДСЧЕТ СТАТИСТИКИ ===');
    console.log('filteredLeaves:', filteredLeaves);
    console.log('selectedYear:', selectedYear, 'selectedMonth:', selectedMonth);
    
    for (const leave of filteredLeaves) {
      console.log('Обрабатываем отгул:', {
        id: leave.id,
        type: leave.type,
        startDate: leave.startDate,
        endDate: leave.endDate,
        reason: leave.reason
      });
      
      if (!processedLeaves.has(leave.id)) {
        const startDate = new Date(leave.startDate);
        const endDate = new Date(leave.endDate);
        const currentMonth = new Date(selectedYear, selectedMonth);
        const nextMonth = new Date(selectedYear, selectedMonth + 1);
        
        console.log('Проверяем даты:', {
          startDate: startDate.toISOString(),
          endDate: endDate.toISOString(),
          currentMonth: currentMonth.toISOString(),
          nextMonth: nextMonth.toISOString(),
          inRange: startDate < nextMonth && endDate >= currentMonth
        });
        
        if (startDate < nextMonth && endDate >= currentMonth) {
          const logicalType = getLogicalType(leave);
          console.log('logicalType:', logicalType);
          
          if (logicalType === 'vacation') stats.vacation++;
          else if (logicalType === 'sick') stats.sick++;
          else if (logicalType === 'remote') stats.remote++;
          else if (logicalType === 'leave') stats.leave++;
          
          processedLeaves.add(leave.id);
          console.log('Добавлен в статистику. Текущие stats:', stats);
        }
      }
    }
    
    console.log('=== ИТОГОВАЯ СТАТИСТИКА ===', stats);
    return stats;
  }, [filteredLeaves, selectedYear, selectedMonth]);

  // Матрица дней
  const daysMatrix = useMemo(() => getDaysMatrix(selectedYear, selectedMonth), [selectedYear, selectedMonth]);

  // Получение отсутствующих на дату
  const getAbsentList = (date) => {
    return filteredLeaves.filter(l => {
      const startDateStr = formatDateToYYYYMMDD(l.startDate);
      const endDateStr = formatDateToYYYYMMDD(l.endDate);
      return isDateInRange(date, startDateStr, endDateStr);
    });
  };

  // Функция удаления отгула
  const handleDeleteLeave = async (leaveId) => {
    console.log('Попытка удалить отгул с ID:', leaveId);
    
    try {
      const url = `/api/leaves/${leaveId}`;
      console.log('Отправляем DELETE запрос на:', url);
      
      // Сначала попробуем получить отгул, чтобы убедиться что он существует
      const checkResponse = await fetch(`/api/leaves/${leaveId}`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      
      if (!checkResponse.ok) {
        throw new Error(`Отгул с ID ${leaveId} не найден`);
      }
      
      const leaveData = await checkResponse.json();
      console.log('Данные отгула перед удалением:', leaveData);

      const response = await fetch(url, {
        method: 'DELETE',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        }
      });

      console.log('Ответ сервера:', response.status, response.statusText);

      if (response.ok) {
        const result = await response.json();
        console.log('Результат удаления:', result);
        
        // Обновляем список отгулов после удаления
        fetch('/api/all-leaves', { headers: { Authorization: `Bearer ${token}` } })
          .then(r => r.json())
          .then(data => {
            console.log('Обновленный список отгулов:', data);
            setLeaves(Array.isArray(data) ? data : []);
          });
        
        // Показываем уведомление об успешном удалении
        alert('Отгул успешно удален');
      } else {
        const errorData = await response.text();
        console.error('Ошибка сервера:', response.status, errorData);
        throw new Error(`HTTP ${response.status}: ${errorData}`);
      }
    } catch (error) {
      console.error('Ошибка при удалении отгула:', error);
      alert(`Ошибка при удалении отгула: ${error.message}`);
    }
  };

  // Загрузка данных
  useEffect(() => {
    if (!open) return;
    
    // Загружаем отгулы
    fetch('/api/all-leaves', { headers: { Authorization: `Bearer ${token}` } })
      .then(r => r.json())
      .then(data => {
        console.log('Загруженные отгулы из API:', data);
        setLeaves(Array.isArray(data) ? data : []);
      });
    
    // Загружаем пользователей
    fetch('/api/users', { headers: { Authorization: `Bearer ${token}` } })
      .then(r => r.json())
      .then(data => {
        const arr = Array.isArray(data) ? data : (Array.isArray(data.users) ? data.users : []);
        setUsers(Object.fromEntries(arr.map(u => [u.id, u])));
      });

    // Сотрудники для отображения дней рождения в ячейках календаря
    fetch('/api/congratulations/employees', { headers: { Authorization: `Bearer ${token}` } })
      .then(r => r.json())
      .then(data => setEmployeesBirthdays(Array.isArray(data) ? data : []))
      .catch(() => setEmployeesBirthdays([]));

    // Получаем текущего пользователя
    let user = null;
    try {
      const userStr = localStorage.getItem('user');
      if (userStr) {
        user = JSON.parse(userStr);
        console.log('🔍 [AllLeavesCalendar] Загружен пользователь из localStorage:', {
          id: user.id,
          username: user.username || (user.first_name + ' ' + user.last_name),
          role: user.role,
          employee_id: user.employee_id
        });
      } else {
        console.warn('🔍 [AllLeavesCalendar] Пользователь не найден в localStorage');
      }
    } catch (e) {
      console.error('🔍 [AllLeavesCalendar] Ошибка при загрузке пользователя:', e);
    }
    setCurrentUser(user);
    
    // Загружаем баланс
    const uid = user?.id;
    if (uid != null) {
      fetch(`/api/leave-balance/${uid}`, { headers: { Authorization: `Bearer ${token}` } })
        .then(r => r.ok ? r.json() : Promise.reject(new Error(`HTTP ${r.status}`)))
        .then(setBalance)
        .catch(()=>setBalance(null));
    } else {
      setBalance(null);
    }
  }, [open, token]);

  // Блокировка скролла
  useEffect(() => {
    if (!open) return;
    const scrollBarWidth = window.innerWidth - document.documentElement.clientWidth;
    const originalOverflow = document.body.style.overflow;
    const originalPaddingRight = document.body.style.paddingRight;
    
    document.body.style.overflow = 'hidden';
    if (scrollBarWidth > 0) {
      document.body.style.paddingRight = `${scrollBarWidth}px`;
    }
    
    return () => {
      document.body.style.overflow = originalOverflow || '';
      document.body.style.paddingRight = originalPaddingRight || '';
    };
  }, [open]);

  // Сброс состояния при закрытии
  useEffect(() => {
    if (!open) {
      setCalendarCollapsed(false);
      setAbsentModal({ open: false, date: null, list: [] });
      setShowAddModal(false);
      setEditLeave(null);
    }
  }, [open]);

  // Загрузка истории
  useEffect(() => {
    if (!open || selectedUser === 'all' || !currentUser) return;
    fetch(`/api/leave-history/${currentUser.id}`, { headers: { Authorization: `Bearer ${token}` } })
      .then(r => r.json()).then(setHistory);
  }, [open, selectedUser, currentUser, token]);

  if (!open) return null;

  return (
    <>
      {open && ReactDOM.createPortal(
        <div
          className="nd-calOverlay"
          style={{ zIndex: 100000 }}
          onClick={onClose}
        >
          <div
            data-calendar-container
            className="nd-calSheet"
            onClick={e=>e.stopPropagation()}
            style={backgroundStyle.backgroundImage && backgroundStyle.backgroundImage !== 'none' ? {
              backgroundImage: backgroundStyle.backgroundImage,
              backgroundSize: backgroundStyle.backgroundSize || 'cover',
              backgroundPosition: backgroundStyle.backgroundPosition || 'center',
              backgroundRepeat: backgroundStyle.backgroundRepeat || 'no-repeat'
            } : undefined}
          >
            <div className="nd-calHead">
              <button type="button" className="nd-backBtn" onClick={onClose} title="Закрыть" aria-label="Закрыть">←</button>
              <h2>
                Общий календарь отпусков и отгулов
                <span>заявки, одобрение, дни рождения · интерактивный</span>
              </h2>
              <div className="nd-calHeadActs">
                <button
                  type="button"
                  className="nd-themeBtn"
                  onClick={toggleNdTheme}
                  title={ndIsLight ? 'Включить тёмную тему' : 'Включить светлую тему'}
                >
                  {ndIsLight ? '🌙' : '☀️'}
                </button>
                <button
                  type="button"
                  className="nd-foldBtn"
                  onClick={()=>setCalendarCollapsed(v=>!v)}
                  title={calendarCollapsed ? 'Показать календарь' : 'Свернуть календарь'}
                >
                  {calendarCollapsed ? '🗗 Развернуть календарь' : '🗕 Свернуть календарь'}
                </button>
              </div>
            </div>

            {/* Заявки на одобрение для HR */}
            {isHr && (() => {
              const pendingLeaves = (Array.isArray(leaves) ? leaves : []).filter(l=>{
                if (l.status !== 'pending') return false;
                if (selectedStatus !== 'all' && l.status !== selectedStatus) return false;
                if (selectedUser !== 'all' && l.userId !== Number(selectedUser)) return false;
                const logicalType = getLogicalType(l);
                if (selectedType !== 'all' && logicalType !== selectedType) return false;
                return true;
              });
              return (
                <div className="nd-appr">
                  <h4>
                    <i />
                    Заявки на одобрение
                    <span className="nd-cntB">{pendingLeaves.length}</span>
                  </h4>
                  {pendingLeaves.length === 0 ? (
                    <div className="nd-apprNone">• Нет заявок на одобрение</div>
                  ) : (
                    <div className="nd-pList">
                      {pendingLeaves.map(l => {
                        const t = ND_LEAVE_TYPES[getLogicalType(l)] || ND_LEAVE_TYPES.leave;
                        return (
                          <div key={l.id} className="nd-pItem">
                            <span className={`nd-pTy ${t.cls}`}>{t.emoji} {t.label}</span>
                            <b>{users[l.userId]?.username || 'Сотрудник'}</b>
                            <span className="nd-pDt">{String(l.startDate).slice(0,10)}{l.endDate !== l.startDate ? ` → ${String(l.endDate).slice(0,10)}` : ''}</span>
                            {l.reason && <span className="nd-pReason">{l.reason}</span>}
                            <span className="nd-pBtns">
                              <button type="button" className="nd-okB" title="Одобрить" onClick={async()=>{
                                await fetch(`/api/leaves/${l.id}/status`, {
                                  method:'PUT',
                                  headers:{'Content-Type':'application/json',Authorization:`Bearer ${token}`},
                                  body:JSON.stringify({status:'approved'})
                                });
                                fetch('/api/all-leaves', { headers: { Authorization: `Bearer ${token}` } })
                                  .then(r => r.json()).then(setLeaves);
                                window.dispatchEvent(new CustomEvent('leaves-worktime-updated'));
                              }}>✓ Утвердить</button>
                              <button type="button" className="nd-noB" title="Отклонить" onClick={async()=>{
                                await fetch(`/api/leaves/${l.id}/status`, {
                                  method:'PUT',
                                  headers:{'Content-Type':'application/json',Authorization:`Bearer ${token}`},
                                  body:JSON.stringify({status:'declined'})
                                });
                                fetch('/api/all-leaves', { headers: { Authorization: `Bearer ${token}` } })
                                  .then(r => r.json()).then(setLeaves);
                              }}>✕ Отклонить</button>
                            </span>
                          </div>
                        );
                      })}
                    </div>
                  )}
                </div>
              );
            })()}

            {/* Фильтры */}
            <div className="nd-fltRow">
              <div className="nd-fGrp">
                <label htmlFor="nd-cal-month">Месяц</label>
                <select id="nd-cal-month" value={selectedMonth} onChange={e=>setSelectedMonth(Number(e.target.value))}>
                  {months.map((m,i)=>(<option key={i} value={i}>{m}</option>))}
                </select>
              </div>

              <div className="nd-fGrp">
                <label htmlFor="nd-cal-year">Год</label>
                <select id="nd-cal-year" value={selectedYear} onChange={e=>setSelectedYear(Number(e.target.value))}>
                  {years.map(y=>(<option key={y} value={y}>{y}</option>))}
                </select>
              </div>

              {isHr && (
                <div className="nd-fGrp">
                  <label htmlFor="nd-cal-emp">Сотрудник</label>
                  <div style={{ display:'flex', alignItems:'center', gap:8 }}>
                    <input
                      id="nd-cal-emp"
                      type="text"
                      value={userSearch}
                      placeholder="Быстрый поиск по имени…"
                      onChange={e=>{ setUserSearch(e.target.value); setShowUserAutocomplete(true); }}
                      onFocus={()=>setShowUserAutocomplete(true)}
                      onBlur={()=>setTimeout(()=>setShowUserAutocomplete(false), 150)}
                      style={{ width: 220 }}
                    />
                    <button
                      type="button"
                      className="nd-tChip"
                      onClick={()=>{ setSelectedUser('all'); setUserSearch(''); }}
                      title="Показать всех"
                    >Все</button>
                  </div>
                  {showUserAutocomplete && (
                    <div className="nd-userAc">
                      {Object.values(users)
                        .filter(u => {
                          const q = userSearch.trim().toLowerCase();
                          if (!q) return true;
                          const first = (u.first_name||'').toLowerCase();
                          const last = (u.last_name||'').toLowerCase();
                          const usern = (u.username||'').toLowerCase();
                          return usern.includes(q) || first.includes(q) || last.includes(q);
                        })
                        .slice(0, 50)
                        .map(u => (
                          <div
                            key={u.id}
                            onMouseDown={()=>{ setSelectedUser(u.id); setUserSearch(u.username || `${u.first_name||''} ${u.last_name||''}`.trim()); setShowUserAutocomplete(false); }}
                          >
                            {u.username || `${u.first_name||''} ${u.last_name||''}`}
                          </div>
                        ))}
                    </div>
                  )}
                </div>
              )}

              <div className="nd-fGrp">
                <label>Тип события</label>
                <div className="nd-typeChips">
                  <button
                    type="button"
                    className={selectedType==='all' ? 'nd-tChip nd-t-all nd-on' : 'nd-tChip nd-t-all'}
                    onClick={()=>setSelectedType('all')}
                  >Все</button>
                  {Object.entries(ND_LEAVE_TYPES).map(([key, t]) => (
                    <button
                      key={key}
                      type="button"
                      className={selectedType===key ? `nd-tChip ${t.cls} nd-on` : `nd-tChip ${t.cls}`}
                      onClick={()=>setSelectedType(key)}
                    >{t.emoji} {t.label}</button>
                  ))}
                </div>
              </div>

              <div className="nd-fGrp">
                <label htmlFor="nd-cal-status">Статус заявки</label>
                <select id="nd-cal-status" value={selectedStatus} onChange={e=>setSelectedStatus(e.target.value)}>
                  <option value="all">Все</option>
                  <option value="approved">Одобрено</option>
                  <option value="pending">Ожидает</option>
                  <option value="completed">Отработано</option>
                  <option value="rejected">Отклонено</option>
                </select>
              </div>
            </div>

            {/* Календарь */}
            {!calendarCollapsed && (
              <div className="nd-calWrap nd-glass">
                <div className="nd-dowGrid">
                  {weekDays.map(d=>(<div key={d} className="nd-dow">{d}</div>))}
                </div>
                <div className="nd-calGrid">
                  {daysMatrix.flat().map((date,i)=>{
                    if (!date) return <div key={i} className="nd-cell nd-out" />;

                    const dayLeaves = filteredLeaves.filter(leave => isDateInRange(
                      date,
                      formatDateToYYYYMMDD(leave.startDate),
                      formatDateToYYYYMMDD(leave.endDate)
                    ));
                    const bdayList = getBirthdaysForDate(date);

                    return (
                      <div
                        key={i}
                        className={isToday(date) ? 'nd-cell nd-today' : 'nd-cell'}
                        style={{ animationDelay: `${Math.min(i * 10, 400)}ms` }}
                        onClick={()=>{
                          const absentList = getAbsentList(date);
                          if (absentList.length > 0) {
                            setAbsentModal({ open: true, date, list: absentList });
                          }
                        }}
                      >
                        <span className="nd-dn">{date.getDate()}</span>

                        {bdayList.length > 0 && (
                          <button
                            type="button"
                            className="nd-ev nd-t-bday"
                            title={`День рождения: ${bdayList.map(e => (e.last_name || e.first_name || e.username || '—').trim()).filter(Boolean).join(', ')}`}
                            onClick={(e) => { e.stopPropagation(); setBirthdayModal({ open: true, date, list: bdayList }); }}
                          >
                            🎂 {bdayList[0].last_name || bdayList[0].first_name || bdayList[0].username || '—'}
                            {bdayList.length > 1 ? ` +${bdayList.length - 1}` : ''}
                          </button>
                        )}

                        {dayLeaves.map(leave => {
                          const t = ND_LEAVE_TYPES[getLogicalType(leave)] || ND_LEAVE_TYPES.leave;
                          const isPending = leave.status === 'pending';
                          const who = users[leave.userId]?.username || 'Сотрудник';
                          return (
                            <button
                              key={leave.id}
                              type="button"
                              className={`nd-ev ${t.cls}${isPending ? ' nd-pend' : ''}`}
                              title={`${t.label}${isPending ? ' (на одобрении)' : ''} · ${who}`}
                              onClick={(e) => { e.stopPropagation(); setAbsentModal({ open: true, date, list: [leave] }); }}
                            >
                              {t.emoji} {who}
                            </button>
                          );
                        })}
                      </div>
                    );
                  })}
                </div>
              </div>
            )}

            {/* Список отгулов в свернутом режиме */}
            {calendarCollapsed && (
              <div className="nd-calWrap nd-glass">
                <h3 style={{margin:'0 0 13px',fontSize:'13px',fontWeight:800,letterSpacing:'.4px',color:'var(--nd-txt)'}}>
                  События в {months[selectedMonth]} {selectedYear}
                </h3>
                <div style={{display:'flex',flexDirection:'column',gap:9,maxHeight:400,overflowY:'auto'}}>
                  {filteredLeaves
                    .filter(leave => {
                      const startDate = new Date(leave.startDate);
                      const endDate = new Date(leave.endDate);
                      const currentMonth = new Date(selectedYear, selectedMonth);
                      const nextMonth = new Date(selectedYear, selectedMonth + 1);
                      return startDate < nextMonth && endDate >= currentMonth;
                    })
                    .sort((a, b) => new Date(a.startDate) - new Date(b.startDate))
                    .map(leave => {
                      const logicalType = getLogicalType(leave);
                      const t = ND_LEAVE_TYPES[logicalType] || ND_LEAVE_TYPES.leave;
                      const typeText = t.label;

                      return (
                        <div 
                          key={leave.id}
                          className={`nd-eRow ${t.cls}`}
                          style={{ cursor: 'pointer' }}
                          onClick={() => {
                            const startDate = new Date(leave.startDate);
                            const endDate = new Date(leave.endDate);
                            const dates = [];
                            for (let d = new Date(startDate); d <= endDate; d.setDate(d.getDate() + 1)) {
                              dates.push(new Date(d));
                            }
                            if (dates.length > 0) {
                              setAbsentModal({ open: true, date: dates[0], list: [leave] });
                            }
                          }}
                        >
                          <span className="nd-ty">{t.emoji} {typeText}</span>

                          <div style={{flex:1,minWidth:160}}>
                            <div className="nd-who" style={{marginBottom:3}}>
                              {users[leave.userId]?.username || 'Сотрудник'}
                            </div>
                            <div className="nd-dt">
                              {String(leave.startDate).slice(0,10)} — {String(leave.endDate).slice(0,10)}
                            </div>
                            
                            {/* Отображение времени отгула */}
                            <div className="nd-dt" style={{marginTop:3}}>
                              {(() => {
                                // Проверяем различные возможные поля для времени
                                const minutes = leave.leaveMinutes || leave.minutes || 0;
                                const hours = leave.leaveHours || leave.hours || leave.requiredHours || 0;
                                
                                // Если есть минуты
                                if (minutes && minutes > 0) {
                                  if (minutes < 60) {
                                    return `⏱️ ${minutes} мин.`;
                                  } else {
                                    const h = Math.floor(minutes / 60);
                                    const m = minutes % 60;
                                    if (m === 0) {
                                      return `⏱️ ${h} ч.`;
                                    } else {
                                      return `⏱️ ${h} ч. ${m} мин.`;
                                    }
                                  }
                                }
                                
                                // Если есть часы
                                if (hours && hours > 0) {
                                  if (hours < 1) {
                                    return `⏱️ ${Math.round(hours * 60)} мин.`;
                                  } else if (hours === Math.floor(hours)) {
                                    return `⏱️ ${hours} ч.`;
                                  } else {
                                    const wholeHours = Math.floor(hours);
                                    const remainingMinutes = Math.round((hours - wholeHours) * 60);
                                    return `⏱️ ${wholeHours} ч. ${remainingMinutes} мин.`;
                                  }
                                }
                                
                                // Если нет времени в минутах/часах, показываем дни
                                const startDate = new Date(leave.startDate);
                                const endDate = new Date(leave.endDate);
                                const diffTime = Math.abs(endDate - startDate);
                                const diffDays = Math.ceil(diffTime / (1000 * 60 * 60 * 24)) + 1;
                                
                                if (diffDays === 1) {
                                  return `📅 1 день`;
                                } else {
                                  return `📅 ${diffDays} дн.`;
                                }
                              })()}
                            </div>

                            {leave.reason && (
                              <div className="nd-rs" style={{marginTop:3}}>
                                {leave.reason}
                              </div>
                            )}
                          </div>

                          <span className={`nd-stChip nd-st-${leave.status === 'approved' ? 'approved' : leave.status === 'pending' ? 'pending' : leave.status === 'completed' ? 'completed' : 'rejected'}`}>
                            {ND_STATUS_LABELS[leave.status] || 'Отклонено'}
                          </span>

                          {(isHr || leave.userId === currentUser?.id) && (
                            <button
                              type="button"
                              className="nd-delB"
                              onClick={(e) => {
                                e.stopPropagation();
                                if (window.confirm(`Вы уверены, что хотите удалить ${typeText.toLowerCase()}?`)) {
                                  handleDeleteLeave(leave.id);
                                }
                              }}
                              title="Удалить"
                            >
                              🗑 Удалить
                            </button>
                          )}
                        </div>
                      );
                    })}
                  
                  {filteredLeaves.filter(leave => {
                    const startDate = new Date(leave.startDate);
                    const endDate = new Date(leave.endDate);
                    const currentMonth = new Date(selectedYear, selectedMonth);
                    const nextMonth = new Date(selectedYear, selectedMonth + 1);
                    return startDate < nextMonth && endDate >= currentMonth;
                  }).length === 0 && (
                    <div className="nd-empty">
                      <div style={{fontSize:'2em',marginBottom:8}}>📅</div>
                      Нет событий в этом месяце
                    </div>
                  )}
                </div>
              </div>
            )}

            {/* Легенда с счетчиками — в самом низу модалки */}
            <div className="nd-legRow">
              {Object.entries(ND_LEAVE_TYPES).map(([key, t]) => (
                <span key={key} className={`nd-leg ${t.cls}`}>
                  <span className="nd-ic">{t.emoji}</span>
                  {t.label}: <b>{monthlyStats[key]} шт.</b>
                </span>
              ))}
              <span className="nd-leg nd-t-bday">
                <span className="nd-ic">🎂</span>
                День рождения
              </span>
            </div>
          </div>
        </div>,
        document.body
      )}

      {/* Модальное окно отсутствующих */}
      {absentModal.open && ReactDOM.createPortal(
        <div className="nd-subModal" onClick={()=>setAbsentModal({ open: false, date: null, list: [] })}>
          <div className="nd-mBox" style={{ width: 720 }} onClick={e=>e.stopPropagation()}>
            <h3>
              <span>Отсутствующие сотрудники</span>
              <button onClick={()=>setAbsentModal({ open: false, date: null, list: [] })} aria-label="Закрыть">✕</button>
            </h3>
            <div className="nd-mSub">{absentModal.date && absentModal.date.toLocaleDateString('ru-RU', { day: 'numeric', month: 'long', year: 'numeric' })}</div>
            <ul className="nd-eList">
              {absentModal.list.map((l, idx) => {
                const logicalType = getLogicalType(l);
                const t = ND_LEAVE_TYPES[logicalType] || ND_LEAVE_TYPES.leave;

                return (
                  <li key={idx} className={`nd-eRow ${t.cls}`}>
                    <span className="nd-ty">{t.emoji} {t.label}</span>
                    <span className="nd-who">{users[l.userId]?.username || 'Сотрудник'}</span>
                    <span className="nd-dt">{String(l.startDate).slice(0,10)} — {String(l.endDate).slice(0,10)}</span>
                    {(() => {
                      const minutes = l.leaveMinutes || l.minutes || 0;
                      const hours = l.leaveHours || l.hours || l.requiredHours || 0;

                      if (minutes && minutes > 0) {
                        if (minutes < 60) return <span className="nd-hChip">⏱ {minutes} мин.</span>;
                        const h = Math.floor(minutes / 60);
                        const m = minutes % 60;
                        return <span className="nd-hChip">⏱ {h}ч {m > 0 ? `${m}м` : ''}</span>;
                      }

                      if (hours && hours > 0) {
                        if (hours < 1) return <span className="nd-hChip">⏱ {Math.round(hours * 60)} мин.</span>;
                        const h = Math.floor(hours);
                        const m = Math.round((hours - h) * 60);
                        return <span className="nd-hChip">⏱ {h}ч {m > 0 ? `${m}м` : ''}</span>;
                      }

                      return null;
                    })()}
                    {l.reason && <span className="nd-rs">{l.reason}</span>}

                    {(isHr || l.userId === currentUser?.id) && (
                      <button
                        type="button"
                        className="nd-delB"
                        onClick={(e) => {
                          e.stopPropagation();
                          if (window.confirm(`Вы уверены, что хотите удалить ${t.label.toLowerCase()}?`)) {
                            handleDeleteLeave(l.id);
                            setAbsentModal({ open: false, date: null, list: [] });
                          }
                        }}
                        title="Удалить"
                      >
                        🗑
                      </button>
                    )}
                  </li>
                );
              })}
            </ul>
          </div>
        </div>,
        document.body
      )}

      {/* Модальное окно именинников по клику на торт */}
      {birthdayModal.open && birthdayModal.list && birthdayModal.list.length > 0 && ReactDOM.createPortal(
        <div className="nd-subModal" onClick={() => setBirthdayModal({ open: false, date: null, list: [] })}>
          <div className="nd-mBox" style={{ width: 420 }} onClick={e => e.stopPropagation()}>
            <h3>
              <span>🎂 Именинники</span>
              <button onClick={() => setBirthdayModal({ open: false, date: null, list: [] })} aria-label="Закрыть">✕</button>
            </h3>
            <div className="nd-mSub">{birthdayModal.date ? birthdayModal.date.toLocaleDateString('ru-RU', { day: 'numeric', month: 'long' }) : ''}</div>
            <ul className="nd-eList">
              {birthdayModal.list.map(e => (
                <li key={e.id} className="nd-eRow nd-t-bday">
                  <span className="nd-ty">🎂</span>
                  <span className="nd-who">
                    {e.last_name || e.first_name || e.username || '—'}
                    {(e.first_name || e.last_name) && e.username ? ` (${e.username})` : ''}
                  </span>
                </li>
              ))}
            </ul>
          </div>
        </div>,
        document.body
      )}
    </>
  );
}
